import { IkeToken, IkeUserRole } from '@ikegps/core-api';
import { IkeRequestError, IkeRequestErrorChecker } from '@ikegps/core-shared';
import { Input } from '../../components/input/input';
import { SelectInputComponent, SelectInputState } from '../../components/input/input.select';
import { InputState, InputStateTypes } from '../../components/input/input.state';
import { ModalFormComponent } from '../../components/modal.form/modal.form.component';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { DefaultTheme } from '../../components/theme';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { Services } from '../../services';
import { AccountsCtrl } from './accounts.ctrl';

const INVITE_SUCCESS = { key: T.ACCOUNTS_INVITE_SUCCESS };
const INVITE_MAX = 10;
const INVITE_EMAIL_ROWS = 3;

const BASIC_ROLE_OPTIONS = [
	{ role: IkeUserRole.USER, label: Translate(T.ROLE_USER) },
	{ role: IkeUserRole.ROOT, label: Translate(T.ROLE_ADMIN) },
];

const EXTRA_ROLE_OPTIONS = [
	{ role: IkeUserRole.VIEWER, label: Translate(T.ROLE_VIEWER) },
	{ role: IkeUserRole.DOWNLOADER, label: Translate(T.ROLE_DOWNLOADER) },
	{ role: IkeUserRole.EDITOR, label: Translate(T.ROLE_EDITOR) },
	{ role: IkeUserRole.DATA_ADMIN, label: Translate(T.ROLE_DATA_ADMIN) },
];

export class AccountsInviteComponent extends ModalFormComponent {
	ctrl: AccountsCtrl;
	email: InputState = new InputState(T.SIGNUP_EMAIL);
	role: SelectInputState = new SelectInputState(T.ACCOUNTS_ROLE);

	constructor(ctrl: AccountsCtrl) {
		super({
			title: T.ACCOUNTS_INVITE_TITLE,
			confirm: T.ACCOUNTS_INVITE,
			success: INVITE_SUCCESS,
		});

		this.ctrl = ctrl;

		this.addState(this.email);
		this.addState(this.role);

		BASIC_ROLE_OPTIONS.forEach((role) => this.role.addOption({ key: role.label, value: role.role }));
		if (Services.Token.has(IkeToken.FEATURE_EXTRA_ROLES) || Services.Token.has(IkeToken.FEATURE_GROUP_IKE_ANALYZE)) {
			EXTRA_ROLE_OPTIONS.forEach((role) => this.role.addOption({ key: role.label, value: role.role }));
		}

		this.email.isRequired = true;
		this.email.type = InputStateTypes.TEXT;

		this.role.isRequired = true;
	}

	get nodes(): MithrilVNode[] {
		return [
			m('span', Translate(T.ACCOUNTS_INVITE_CONFIRM)),
			m(
				'div',
				{ style: 'color:' + DefaultTheme.font.color.error },
				this.role.isValid ? '' : Translate(T.ACCOUNTS_INVITE_CHOOSE_USER_ROLE)
			),
			SelectInputComponent.view(this.role),
			Input.view(this.email, { rows: INVITE_EMAIL_ROWS }),
		];
	}

	override async submit() {
		const emails = this.getEmails();
		const role = this.role.value as IkeUserRole;
		await this.ctrl.inviteUsers(emails, role);
	}

	override failToChange(e: IkeRequestError) {
		this.email.setError(T.EMAIL_INVALID);

		var existingEmails: string[] = [];
		var invalidEmails: string[] = [];
		if (IkeRequestErrorChecker.isValidationFailed(e)) {
			for (const error of e.errors) {
				if (error.codes === 'already_exists') {
					existingEmails.push(error.message);
				} else if (error.codes === 'invalid') {
					invalidEmails.push(error.message);
				}
			}
		}

		if (existingEmails.length > 0) {
			NotificationService.warn({
				key: T.ACCOUNTS_INVITE_EXISTING_USER,
				attrs: existingEmails,
			});
		}

		if (invalidEmails.length > 0) {
			NotificationService.error({
				key: T.ACCOUNTS_INVITE_INVALID_USER,
				attrs: invalidEmails,
			});
		}

		this.email.setSelected(true);
	}

	override validate() {
		const emails = this.getEmails();
		if (!this.email.isFilled || emails.length === 0) {
			this.email.setError(T.EMAIL_INVALID);
		} else if (emails.length > INVITE_MAX) {
			this.email.setError(Translate(T.ACCOUNTS_INVITE_MAX, INVITE_MAX));
		} else {
			this.email.setError('');
		}
	}

	private getEmails(): string[] {
		if (this.email.value == null) {
			return [];
		}
		const emails = this.email.value.split(/[,\n\r]/);

		const output: string[] = [];
		for (const email of emails) {
			const toAdd = email.toLowerCase().trim();
			if (output.indexOf(toAdd) > -1) {
				continue;
			}
			if (toAdd.indexOf('@') === -1) {
				continue;
			}
			output.push(toAdd);
		}

		return output;
	}
}
